import React, { useMemo, useRef, useState } from "react";
import DocumentTextField from "components/foundation/DocumentTextField/DocumentTextField";
import { useFieldArray, useFormContext } from "react-hook-form";
import styled from "styled-components";
import Button from "components/foundation/Button";
import { CloseIcon, PlusIcon } from "assets/icons";
import Divider from "components/foundation/Divider";
import {
  ArrayFieldResponse,
  CalculatedFieldResponse,
} from "models/document/DocumentFieldType";
import formatMinMaxLengthMessage from "utils/formatUtils/formatMinMaxLengthMessage";
import { get } from "lodash-es";
import formatNumber from "utils/formatUtils/formatNumber";
import Select from "components/foundation/Select/Select";
interface DocumentArrayFieldProps {
  fieldMetaInfo: ArrayFieldResponse;
  sumFieldMetaInfo?: CalculatedFieldResponse;
  sumFieldName?: string;
}

const DocumentArrayField = ({
  fieldMetaInfo,
  sumFieldMetaInfo,
  sumFieldName,
}: DocumentArrayFieldProps) => {
  const { itemFields } = fieldMetaInfo;

  const newField = useMemo(() => {
    return itemFields.reduce(
      (obj, item) => ({
        ...obj,
        [item.key]: undefined,
      }),
      {},
    );
  }, [itemFields]);

  const unitOptions = [
    { label: "원", value: "1" },
    { label: "천 원", value: "1000" },
    { label: "백만 원", value: "1000000" },
  ];

  /**
   * @description 실제 값을 단위에 따른 표시 값으로 변환
   */
  const getDisplayValue = (actualValue: number, unit: string): string => {
    if (!actualValue) return "";
    const unitValue = Number(unit);
    const displayValue = actualValue / unitValue;
    const formattedValue =
      displayValue % 1 === 0
        ? displayValue.toString()
        : displayValue.toFixed(1);
    return formatNumber(formattedValue);
  };

  /**
   * @description 표시 값과 단위를 실제 값으로 변환
   */
  const getActualValue = (displayValue: string, unit: string): number => {
    if (!displayValue) return 0;
    // 천 자리 구분자 제거
    const cleanValue = displayValue.replace(/,/g, "");
    const numericValue = parseFloat(cleanValue);
    const unitValue = Number(unit);
    return Math.round(numericValue * unitValue);
  };

  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useFormContext();
  const { fields, append, remove } = useFieldArray({
    control,
    name: fieldMetaInfo.key,
  });

  /**
   * @description 추가 버튼 클릭 이벤트 핸들러
   */
  const onClickAddButton = () => {
    append(newField);
  };

  /* 합계 계산 로직 */

  /**
   * @description 합계 계산 함수
   */
  const calculateTotalAmount = () => {
    if (!sumFieldMetaInfo) return;

    const expenseDetails = watch(fieldMetaInfo.key);
    const totalAmount = expenseDetails?.reduce(
      (acc: number, field: any) => acc + (Number(field?.amount) || 0),
      0,
    );

    setValue(sumFieldMetaInfo.key, totalAmount);

    // 화면 표시값 업데이트
    setDisplaySumValue(getDisplayValue(totalAmount, displaySumUnit));
  };

  useMemo(() => {
    if (fields.length === 0) {
      onClickAddButton();
    }
  }, [fields]);

  const lastInputValue = useRef("");

  // 합계 인풋 상태
  const [displaySumValue, setDisplaySumValue] = useState("");
  const [displaySumUnit, setDisplaySumUnit] = useState("1");

  // 계산 인풋 상태 (각 행별로 관리)
  const [displayAmountUnits, setDisplayAmountUnits] = useState<{
    [key: string]: string;
  }>({});

  /**
   * @description 단위 변경 핸들러
   */
  const handleUnitChange = (rowIndex: number, newUnit: string) => {
    if (rowIndex === 0) {
      // 첫 번째 행 단위 변경 시 모든 행의 단위를 동일하게 변경
      const newUnits: { [key: string]: string } = {};
      fields.forEach((_, index) => {
        newUnits[index.toString()] = newUnit;
      });
      setDisplayAmountUnits(newUnits);
      setDisplaySumUnit(newUnit);

      // 합계 표시값도 새로운 단위로 업데이트
      if (sumFieldMetaInfo) {
        const currentTotal = watch(sumFieldMetaInfo.key) || 0;
        setDisplaySumValue(getDisplayValue(currentTotal, newUnit));
      }
    } else {
      setDisplayAmountUnits((prev) => ({ ...prev, [rowIndex]: newUnit }));
    }
  };

  return (
    <ArrayFieldWrapStyle>
      {fields.map((field, index) => (
        <RowWrapStyle key={field.id}>
          {itemFields.map((itemField: any) => {
            const fieldName = `${fieldMetaInfo.key}.${index}.${itemField.key}`;
            const isItemToSum = itemField.key === sumFieldName;

            const commonRules = {
              required:
                itemField.key === "amount"
                  ? false
                  : typeof itemField.required === "string"
                    ? itemField.required
                    : "필수",
              minLength: itemField.minLength
                ? {
                    value: itemField.minLength,
                    message: formatMinMaxLengthMessage(
                      itemField.minLength,
                      itemField.maxLength,
                    ),
                  }
                : undefined,
              maxLength: itemField.maxLength
                ? {
                    value: itemField.maxLength,
                    message: formatMinMaxLengthMessage(
                      itemField.minLength,
                      itemField.maxLength,
                    ),
                  }
                : undefined,
              validate: itemField.validate,
            };

            return !isItemToSum ? (
              <DocumentTextField
                key={itemField.key}
                placeholder={itemField.label}
                type={itemField.type}
                {...(isItemToSum
                  ? register(fieldName, {
                      ...commonRules,
                      min: itemField.min,
                      max: itemField.max,
                      onBlur: () => {
                        calculateTotalAmount();
                      },
                    })
                  : register(fieldName, {
                      ...commonRules,
                    }))}
                errorMessage={
                  get(errors, `${fieldMetaInfo.key}.${index}.${itemField.key}`)
                    ?.message as string
                }
              />
            ) : (
              <AmountContainer>
                <DocumentTextField
                  key={itemField.key}
                  placeholder={itemField.label}
                  inputStyle={{ textAlign: "right" }}
                  value={(() => {
                    const actualValue = watch(fieldName) || 0;
                    const currentUnit =
                      displayAmountUnits[index.toString()] || "1";
                    return getDisplayValue(actualValue, currentUnit);
                  })()}
                  {...(isItemToSum
                    ? register(fieldName, {
                        ...commonRules,
                        min: itemField.min,
                        max: itemField.max,
                        validate: (value) => {
                          if (lastInputValue.current.includes("-"))
                            return "1 이상의 금액 입력";
                          if (!value) return "필수";
                          return true;
                        },
                        onChange: (event) => {
                          const value = event.target.value;
                          lastInputValue.current = value;

                          // 소수점과 천 자리 구분자 포함 숫자만 허용
                          const cleanValue = value.replace(/[^0-9.,-]/g, "");
                          const currentUnit =
                            displayAmountUnits[index.toString()] || "1";
                          const actualValue = getActualValue(
                            cleanValue,
                            currentUnit,
                          );

                          // 실제 값 저장
                          setValue(fieldName, actualValue.toString(), {
                            shouldValidate: true,
                          });
                        },
                        onBlur: (event) => {
                          const value = event.target.value;
                          lastInputValue.current = value;

                          // 소수점과 천 자리 구분자 포함 숫자만 허용
                          const cleanValue = value.replace(/[^0-9.,-]/g, "");
                          const currentUnit =
                            displayAmountUnits[index.toString()] || "1";
                          const actualValue = getActualValue(
                            cleanValue,
                            currentUnit,
                          );

                          // 실제 값(원 단위)을 폼에 저장
                          setValue(fieldName, actualValue.toString(), {
                            shouldValidate: true,
                          });
                          calculateTotalAmount();
                        },
                      })
                    : register(fieldName, {
                        ...commonRules,
                      }))}
                  errorMessage={
                    get(
                      errors,
                      `${fieldMetaInfo.key}.${index}.${itemField.key}`,
                    )?.message as string
                  }
                />
                <Select
                  style={{ width: "12rem", height: "5.5rem" }}
                  value={displayAmountUnits[index.toString()] || "1"}
                  placeholder="원"
                  list={unitOptions}
                  onValueChange={(value) => {
                    handleUnitChange(index, value);
                  }}
                />
              </AmountContainer>
            );
          })}

          {index !== 0 ? (
            <RemoveButton
              onClick={() => {
                remove(index);
                calculateTotalAmount();
              }}
            >
              <CloseIcon />
            </RemoveButton>
          ) : (
            <div style={{ width: "2.4rem" }}></div>
          )}
        </RowWrapStyle>
      ))}

      <ArrayFooterActionsStyle>
        <Button
          variant="ghost"
          size="small"
          leftIcon={<PlusIcon />}
          onClick={onClickAddButton}
        >
          추가
        </Button>
      </ArrayFooterActionsStyle>

      {sumFieldMetaInfo && (
        <>
          <Divider />

          <InlineWrapStyle>
            <label>합계</label>
            <DocumentTextField
              placeholder="0"
              readOnly
              value={
                displaySumValue || formatNumber(watch(sumFieldMetaInfo.key))
              }
              inputStyle={{ textAlign: "right" }}
              {...register(sumFieldMetaInfo.key)}
            />
            <Select
              style={{ width: "12rem", height: "5.5rem" }}
              placeholder="원"
              value={displaySumUnit}
              list={unitOptions}
              onValueChange={(value) => {
                setDisplaySumUnit(value);
                // 현재 합계를 새로운 단위로 변환해서 표시
                const currentTotal = watch(sumFieldMetaInfo.key) || 0;
                setDisplaySumValue(getDisplayValue(currentTotal, value));
              }}
            />
          </InlineWrapStyle>
        </>
      )}
    </ArrayFieldWrapStyle>
  );
};

export default DocumentArrayField;

/* 필드 스타일 */
const ArrayFieldWrapStyle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const RowWrapStyle = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr 1fr 1fr 0.2fr;
  gap: 1rem;

  & > *:not(:last-child) {
    flex: 1;
  }
`;

const RemoveButton = styled.button`
  height: 5.6rem;
  flex-shrink: 0;
`;

const ArrayFooterActionsStyle = styled.div`
  display: flex;
`;

const InlineWrapStyle = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  label {
    width: 17rem;
    ${({ theme }) => theme.typography.body1.regular};
    color: ${({ theme }) => theme.color.text.default};
  }
`;

const AmountContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.62rem;
`;
